'use strict';

// external imports
import omniclone from 'omniclone';
import {keys} from 'ts-transformer-keys';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import PropertySet from '~/shared/src/mm/PropertySet';
import Item from '~/shared/src/ci/Item';
import Property, {
    PropertyAttributes,
    PropertyJsonAttributes,
    PropertyAttributesForCreation,
    PropertyAttributesForUpdate,
    PropertyAttributesSettable,
    isPropertyAttributes,
    isPropertyJsonAttributes
} from '~/shared/src/ci/Property';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {
    ItemMetaDataMapping,
    PropertyMetaDataMapping
} from '~/shared/src/utilities/modules/data';


// type definitions
export type EnumPropertyAttributes = {
    itemId : number;
    propertySetId : number;
    properties? : ( PropertyAttributes & Required<Pick<PropertyAttributes, 'key'>> )[];
};
export const isEnumPropertyAttributes = ( enumPropertyAttributesToTest : any ) : enumPropertyAttributesToTest is EnumPropertyAttributes => {
    let retVal = false;

    try {
        if ( typeof enumPropertyAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( enumPropertyAttributesToTest.itemId === undefined || typeof enumPropertyAttributesToTest.itemId !== 'number' ) {
            throw new Error();
        }
        if ( enumPropertyAttributesToTest.propertySetId === undefined || typeof enumPropertyAttributesToTest.propertySetId !== 'number' ) {
            throw new Error();
        }

        if ( enumPropertyAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( enumPropertyAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyAttributes of enumPropertyAttributesToTest.properties ) {
                if ( !isPropertyAttributes( { itemId: 1, ...propertyAttributes } as any ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type EnumPropertyJsonAttributes = Omit<EnumPropertyAttributes, 'properties'>
    & { properties? : ( PropertyJsonAttributes & Required<Pick<PropertyJsonAttributes, 'key'>> )[] };
export const isEnumPropertyJsonAttributes = ( enumPropertyJsonAttributesToTest : any ) : enumPropertyJsonAttributesToTest is EnumPropertyJsonAttributes => {
    let retVal = false;

    try {
        if ( typeof enumPropertyJsonAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( enumPropertyJsonAttributesToTest.itemId === undefined || typeof enumPropertyJsonAttributesToTest.itemId !== 'number' ) {
            throw new Error();
        }
        if ( enumPropertyJsonAttributesToTest.propertySetId === undefined || typeof enumPropertyJsonAttributesToTest.propertySetId !== 'number' ) {
            throw new Error();
        }

        if ( enumPropertyJsonAttributesToTest.properties !== undefined ) {
            if ( !Array.isArray( enumPropertyJsonAttributesToTest.properties as any ) ) {
                throw new Error();
            }

            for ( const propertyJsonAttributes of enumPropertyJsonAttributesToTest.properties ) {
                if ( !isPropertyJsonAttributes( propertyJsonAttributes ) ) {
                    throw new Error();
                }
            }
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type EnumPropertyOnlyAttributesSettable = Pick<PropertyAttributes, never>;
export const enumPropertyOnlyAttributesSettable = keys<EnumPropertyOnlyAttributesSettable>() as string[];
export type EnumPropertyAttributesSettable = EnumPropertyOnlyAttributesSettable
    & { properties? : { [ key : string ] : PropertyAttributesSettable } };
export const enumPropertyAttributesSettable = keys<EnumPropertyAttributesSettable>() as string[];

export type EnumPropertyOnlyAttributesForCreation = Required<Pick<EnumPropertyAttributes, 'itemId' | 'propertySetId'>>;
export type EnumPropertyAttributesForCreation = EnumPropertyOnlyAttributesForCreation
    & { properties? : PropertyAttributesForCreation[] };

export type EnumPropertyOnlyAttributesForUpdate = Pick<PropertyAttributes, never>;
export type EnumPropertyAttributesForUpdate = EnumPropertyOnlyAttributesForUpdate
    & { properties? : ( PropertyAttributesForUpdate | PropertyAttributesForCreation )[] };


/**
 * The EnumProperty class.
 */
class EnumProperty {


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ItemId the EnumProperty belongs to.
     */
    protected _itemId : number;
    get itemId() : number {
        return this._itemId;
    }


    /**
     * The PropertySetId of the EnumProperty.
     */
    protected _propertySetId : number;
    get propertySetId() {
        return this._propertySetId;
    }


    /*** ALIASES ***********************************************************************************************************************/

    /**
     * The PropertySet of this EnumProperty.
     */
    get propertySet() : PropertySet {
        return metaModel[ this._schema ].getPropertySet( this._propertySetId )!;
    }


    /*************************************************************************************************************************************/

    /**
     * The Item this EnumProperty belongs to
     */
    protected _parent : Item;
    get parent() {
        return this._parent;
    }

    set parent( parent : Item ) {
        if ( this._parent instanceof Item && parent instanceof Item ) {
            this._parent = parent;
        }
    }

    /**
     * The database schema this EnumProperty belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * An map containing the Properties by PropertyKey.
     */
    protected _properties : Map<string, Property>;
    get properties() {
        return this._properties;
    }

    protected _isReorderingProperties : boolean = false;
    get isReorderingProperties() {
        return this._isReorderingProperties;
    }

    /**
     *The constructor.
     *
     * @param parent                        The parent Item this belongs to
     * @param attributes                    An object containing attribute-value pairs for this
     *
     */
    constructor( parent : Item, attributes : EnumPropertyAttributes ) {

        if ( attributes.itemId === 0 ) {
            throw new Error( 'YN.base.dataObject.property.attributes.validation.itemId.isEmpty' );
        }

        if ( attributes.propertySetId === 0 ) {
            throw new Error( 'YN.base.dataObject.property.attributes.validation.propertySetId.isEmpty' );
        }

        if ( attributes.propertySetId !== metaModel[ parent.schema ].getPropertySet( attributes.propertySetId )?.id ) {
            throw new LocalizableError(
                'YN.base.dataObject.property.attributes.validation.propertySetId.invalid',
                { propertySetId: String( attributes.propertySetId ), schema: parent.schema }
            );
        }

        if ( !metaModel[ parent.schema ].getPropertySet( attributes.propertySetId )?.isEnumerated ) {
            throw new LocalizableError( 'YN.base.dataObject.enumProperty.attributes.validation.propertySetId.notEnumerated' )
        }

        this._parent = parent;

        this._schema = parent.schema;

        this._itemId = attributes.itemId;
        this._propertySetId = attributes.propertySetId;

        this._properties = new Map();
        if ( attributes.properties ) {
            this.addPropertiesFromJSON( attributes.properties );
        }
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes=all] The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : EnumPropertyJsonAttributes {
        // required attributes
        const retVal : EnumPropertyJsonAttributes = {
            itemId       : this._itemId,
            propertySetId: this._propertySetId
        };

        if ( this._properties.size ) {
            const properties : EnumPropertyJsonAttributes[ 'properties' ] = [];

            for ( const property of this._properties.values() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties.push( property.toJSON( includeAttributes ) as Required<EnumPropertyJsonAttributes>[ 'properties' ][number] );
                }
            }

            if ( properties.length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param [includeAttributes=all] The state of the attributes to include in result.
     */
    toUpdateJSON( includeAttributes : 'all' | 'modified' = 'all' ) : EnumPropertyAttributesSettable {
        // required attributes
        const retVal : EnumPropertyAttributesSettable = {};

        if ( this._properties.size ) {
            const properties : EnumPropertyAttributesSettable[ 'properties' ] = {};

            for ( const [ key, property ] of this._properties.entries() ) {
                if ( property.isNew() || property.isModified() || includeAttributes !== 'modified' ) {
                    properties[ key ] = property.toUpdateJSON( includeAttributes );
                }
            }

            if ( Object.keys( properties ).length ) {
                retVal.properties = properties;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `EnumProperty '${this.propertySet.name}' of ${this._parent}`;
    }

    /**
     * Returns a string containing the XML representation of this
     * @param {boolean} [excludeDeleted]
     */
    toXML( excludeDeleted : boolean = false, indent : number = 0 ) : string {
        const properties : Property[] = [ ...this._properties.values() ];

        let retVal = '';

        properties.sort( ( property1, property2 ) => {
            if ( property1.key && property2.key ) {
                if ( property1.key > property2.key ) {
                    return 1;
                }
                else if ( property1.key < property2.key ) {
                    return -1;
                }
            }

            return 0;
        } );

        for ( const property of properties ) {
            if ( !excludeDeleted || !property.isDeleted() ) {
                if ( retVal ) {
                    retVal += '\r\n';
                }
                retVal += property.toXML( excludeDeleted, indent );
            }
        }

        return retVal;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() : EnumProperty {
        // reset all properties
        for ( const property of this._properties.values() ) {
            // remove new property
            if ( property.isNew() ) {
                this._properties.delete( property.stringKey );
            }
            // reset existing property
            else {
                property.reset();
            }
        }

        return this;
    }

    /**
     * Commits all changes of settable attributes.
     */
    commit( itemMetaDataMapping? : ItemMetaDataMapping, propertyMetaDataMapping? : PropertyMetaDataMapping ) : EnumProperty {

        // update itemId if new and mapped one is given
        if ( this.isNew() && itemMetaDataMapping?.[ this._itemId ]?.id ) {
            this._itemId = itemMetaDataMapping[ this._itemId ].id;
        }

        //  commit all Properties
        for ( const property of this._properties.values() ) {
            property.commit( propertyMetaDataMapping );
        }

        return this;
    }

    /**
     * Apply given attributes to this enum property.
     *
     * @param [attributes] An object containing attribute-value pairs to be applied to the clone
     * @param [clone]      true/false to use clone/update functions of properties
     */
    update( attributes : EnumPropertyAttributesSettable = {}, clone : boolean = false ) : EnumProperty {
        const deletedStatusIds = metaModel[ this._schema ].getDeletedStatus().map( s => s.id );

        // handle properties
        if ( attributes.properties ) {
            for ( const stringKey of Object.keys( attributes.properties ) as string[] ) {
                const propertyAttributes = attributes.properties[ stringKey ];

                let property = this._properties.get( stringKey );

                // clone existing property first if
                if ( clone && property && !property.isNew() ) {
                    property = property.clone();

                    // update property in map
                    this._properties.set( stringKey, property );
                }

                // add property
                if ( !property && ( propertyAttributes.statusId === undefined || !deletedStatusIds.includes( propertyAttributes[ 'statusId' ] ) ) ) {
                    this.addPropertyFromJSON( {
                        itemId       : this._itemId,
                        propertySetId: this._propertySetId,
                        ...propertyAttributes
                    } as EnumPropertyAttributes );
                }

                // delete property
                else if (
                    property
                    && !property.isDeleted()
                    && propertyAttributes.statusId !== undefined
                    && deletedStatusIds.includes( propertyAttributes[ 'statusId' ] )
                ) {
                    // remove new property
                    if ( property.isNew() ) {
                        this._properties.delete( stringKey );

                        this.reorderProperties();
                    }

                    // delete existing property
                    else {
                        property.reset().update( { statusId: propertyAttributes.statusId } );
                    }
                }

                // normal property update
                else if ( property ) {
                    property = property.update( propertyAttributes );
                }
            }
        }

        return this;
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : EnumPropertyAttributesSettable = {} ) : EnumProperty {
        const clone : EnumProperty = omniclone(
            this,
            {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            },
            ( node : any ) => {
                // do not clone parent and properties
                if ( node instanceof Item || node instanceof Property ) {
                    return node;
                }
                else {
                    return undefined;
                }
            }
        );

        // clone and update parent in properties
        for ( const stringKey of clone.properties.keys() ) {
            const property = clone.properties.get( stringKey )!.clone();

            property.parent = clone;

            clone.properties.set( stringKey, property );
        }

        clone.update( attributes, true );

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        let retVal = true;

        // check if one property is not new
        for ( const property of this._properties.values() ) {
            if ( !property.isNew() ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    /**
     * Checks if a Property of this is modified.
     */
    isModified() : boolean {
        let retVal = false;

        // check if one property is modified
        for ( const property of this._properties.values() ) {
            if ( property.isModified() || property.isNew() ) {
                retVal = true;
                break;
            }
        }

        return retVal;
    }

    /**
     * Checks if all Properties of this are valid.
     */
    isValid() : boolean {
        let retVal = true;

        // check if one property is not valid
        for ( const property of this._properties.values() ) {
            if ( !property.isValid() ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    isKeyUnique( key : AttributeValue, property? : Property ) : boolean {
        let retVal : boolean = true;

        // check if key is unique within properties
        for ( const otherProperty of this._properties.values() ) {
            if (
                !otherProperty.isDeleted()
                && !( otherProperty === property || otherProperty.id === property?.id )
                && !( property && property.orderNum !== otherProperty.orderNum )
                && otherProperty.key === key
            ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    isOrderNumUnique( orderNum : number, property? : Property ) : boolean {
        let retVal : boolean = true;

        // check if ordeNum is unique within properties
        for ( const otherProperty of this._properties.values() ) {
            if (
                !otherProperty.isDeleted()
                && !( otherProperty === property || otherProperty.id === property?.id )
                && otherProperty.orderNum === orderNum
            ) {
                retVal = false;
                break;
            }
        }

        return retVal;
    }

    getUniqueKeys() : AttributeValue[] {
        const retVal : AttributeValue[] = [];

        for ( const property of this._properties.values() ) {
            const key = property.key;

            if ( !property.isDeleted() && !retVal.includes( key ) ) {
                retVal.push( key );
            }
        }

        return retVal;
    }

    getUniqueValues() : AttributeValue[] {
        const retVal : AttributeValue[] = [];

        for ( const property of this._properties.values() ) {
            const value = property.value;

            if ( !property.isDeleted() && !retVal.includes( value ) ) {
                retVal.push( value );
            }
        }

        return retVal;
    }

    getMaxOrderNum() : number {
        let retVal : number = 0;

        // get max orderNum from properties
        for ( const property of this._properties.values() ) {
            if ( !property.isDeleted() && property.orderNum > retVal ) {
                retVal = property.orderNum;
            }
        }

        return retVal;
    }

    /*** PROPERTIES RELATED **************************************************************************************************************/

    /**
     * Adds Properties given by data to this.
     *
     * @param dataObject            An array of propertyData
     */
    addPropertiesFromJSON( dataObject : Partial<PropertyAttributes>[] ) {
        for ( const propertyData of dataObject ) {
            this.addPropertyFromJSON( propertyData );
        }
    }

    /**
     * Adds a Property given by data to this.
     *
     * @param dataObject            An object containing attribute-value pairs
     */
    addPropertyFromJSON( dataObject : Partial<PropertyAttributes> ) : Property {
        const propertySet = this.propertySet;

        let newKey : string,
            newValue : AttributeValue | undefined = dataObject.value;

        // convert to string if not a string or ordered by key and not matching
        if (
            typeof dataObject.key !== 'string'
            || (
                propertySet.isKeyOrdered
                && propertySet.keyFromStringRegExp
                && !propertySet.keyFromStringRegExp.exec( dataObject.key )
            )
        ) {
            newKey = propertySet.getStringKey( { key: dataObject.key ?? '', orderNum: this.getMaxOrderNum() + 1 } );
        }
        else {
            newKey = dataObject.key;
        }

        if ( this._properties.has( newKey ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.enumProperty.addProperty.exists',
                { key: newKey, propertyTypeName: propertySet.name }
            );
        }

        // convert only to string if ordered by value and not a string or not matching
        if (
            propertySet.isValueOrdered
            && (
                typeof dataObject.value !== 'string'
                || (
                    propertySet.valueFromStringRegExp
                    && !propertySet.valueFromStringRegExp.exec( dataObject.value )
                )
            )
        ) {
            newValue = propertySet.getStringValue( { value: dataObject.value ?? '', orderNum: this.getMaxOrderNum() + 1 } );
        }

        const newProperty = new Property( this, { ...dataObject, propertySetId: this._propertySetId, itemId: this._itemId, key: newKey, value: newValue } );

        this._properties.set( newProperty.stringKey, newProperty );

        return newProperty;
    }

    /**
     * Adds a new Property by key to this.
     *
     * @param key
     */
    addProperty( key : AttributeValue ) : Property {
        const propertySet     = this.propertySet,
              newKey : string = propertySet.getStringKey( { key: key, orderNum: this.getMaxOrderNum() + 1 } );

        if ( this._properties.has( newKey ) ) {
            throw new LocalizableError(
                'YN.base.dataObject.enumProperty.addProperty.exists',
                { key: newKey, propertyTypeName: propertySet.name }
            );
        }

        const newProperty = new Property( this, { itemId: this._itemId, propertySetId: this._propertySetId, key: newKey } );

        this._properties.set( newKey, newProperty );

        return newProperty;
    }

    updatePropertyKey( property : Property, newStringKey? : string ) {
        if ( !newStringKey ) {
            newStringKey = property.stringKey;
        }

        for ( const [ oldStringKey, propertyToUpdate ] of this._properties ) {
            if ( propertyToUpdate === property ) {
                if ( oldStringKey !== newStringKey ) {
                    this._properties.delete( oldStringKey );
                    this._properties.set( newStringKey, property );
                }
                break;
            }
        }
    }

    reorderProperties( property? : Property, orderNum? : number, clone : boolean = false ) {
        this._isReorderingProperties = true;

        const propertySet                                                     = this.propertySet,
              propertiesToOrder : { stringKey : string; orderNum : number }[] = [];

        if ( !this.parent.isDeleted() && ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) ) {

            // get all non-deleted properties without property to update
            for ( const [ stringKey, propertyToOrder ] of this._properties.entries() ) {
                if ( !propertyToOrder.isDeleted() && propertyToOrder !== property ) {
                    propertiesToOrder.push( {
                        stringKey,
                        orderNum: propertyToOrder.orderNum
                    } );
                }
            }

            // sort by orderNum
            propertiesToOrder.sort( ( p1, p2 ) => {
                if ( p1.orderNum > p2.orderNum ) {
                    return 1;
                }
                else if ( p1.orderNum < p2.orderNum ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            // insert property to update at new orderNum
            if ( property && orderNum ) {
                propertiesToOrder.splice( orderNum - 1, 0, { stringKey: property.stringKey, orderNum: orderNum } );
            }

            // move away if orderNum not the same as in array and not unique
            for ( let i = 0; i < propertiesToOrder.length; i++ ) {
                const newOrderNum = i + 1;

                let propertyToOrder = this._properties.get( propertiesToOrder[ i ].stringKey );

                if ( propertyToOrder && propertyToOrder.orderNum !== newOrderNum ) {
                    if ( clone && propertyToOrder !== property ) {
                        propertyToOrder = propertyToOrder.clone();

                        // update property in map
                        this._properties.set( propertiesToOrder[ i ].stringKey, propertyToOrder );
                    }

                    propertyToOrder.update( { orderNum: this.getMaxOrderNum() + 1 } );

                    propertiesToOrder[ i ].stringKey = propertyToOrder.stringKey;
                }
            }

            // update orderNum if not the same as in array
            for ( let i = 0; i < propertiesToOrder.length; i++ ) {
                const newOrderNum = i + 1;

                let propertyToOrder = this._properties.get( propertiesToOrder[ i ].stringKey );

                if ( propertyToOrder && propertyToOrder.orderNum !== newOrderNum ) {
                    if ( clone && propertyToOrder !== property ) {
                        propertyToOrder = propertyToOrder.clone();

                        // update property in map
                        this._properties.set( propertiesToOrder[ i ].stringKey, propertyToOrder );
                    }

                    propertyToOrder.update( { orderNum: newOrderNum } );
                }
            }
        }

        this._isReorderingProperties = false;
    }
}

export default EnumProperty;
